import { useMemo } from "react";
import { useApp, useUi } from "@/app/AppContext";
import { ALL_LOCATIONS_ID, type SystemLocation } from "@/domain/systemAnalysis";
import {
  buildLocationHistory,
  periodOptions,
  type LocationHistory,
  type PeriodOption,
} from "@/domain/systemAnalysisTrend";

const ALL_LOCATIONS: SystemLocation = {
  locationId: ALL_LOCATIONS_ID,
  locationName: { no: "Alle lokasjoner", en: "All locations" },
};

/** All analyses (one per year) of one location, from the already loaded system data. */
export function useLocationAnalyses(locationId: string): LocationHistory {
  const { data } = useApp();
  return useMemo(
    () => buildLocationHistory(data.systemData.analyses, locationId),
    [data.systemData, locationId],
  );
}

export interface SystemOverTime {
  /** The experimental feature is switched on. */
  available: boolean;
  /** Switched on and the "Over time" view is selected. */
  active: boolean;
  /** Every location with an analysis in any year, after "all locations". */
  locations: SystemLocation[];
  location: SystemLocation;
  history: LocationHistory;
  periods: PeriodOption[];
  /** The years shown; null with a single year (first analysis) or no data. */
  period: PeriodOption | null;
}

export function useSystemOverTime(): SystemOverTime {
  const { data } = useApp();
  const { system } = useUi();
  const available = data.settings.experimentalFeatures.systemAnalysisOverTime;

  const locations = useMemo(() => {
    const byId = new Map<string, SystemLocation>();
    for (const analysis of data.systemData.analyses) {
      for (const location of analysis.locations) {
        if (location.locationId !== ALL_LOCATIONS_ID && !byId.has(location.locationId))
          byId.set(location.locationId, location);
      }
    }
    const sorted = [...byId.values()].sort((a, b) =>
      a.locationId.localeCompare(b.locationId, undefined, { sensitivity: "base", numeric: true }),
    );
    return [ALL_LOCATIONS, ...sorted];
  }, [data.systemData]);

  const wanted = system.locationId.toLowerCase();
  const location =
    locations.find((entry) => entry.locationId.toLowerCase() === wanted) ?? locations[0]!;
  const history = useLocationAnalyses(location.locationId);
  const periods = useMemo(() => periodOptions(history.years), [history.years]);
  const period = periods.find((option) => option.id === system.period) ?? periods[0] ?? null;

  return {
    available,
    active: available && system.mode === "overTime",
    locations,
    location,
    history,
    periods,
    period,
  };
}
